// src/components/circles/CircleInviteCard.jsx
//
// The circle's invite code, how to share it, and how to kill it.
//
// Having the code IS the invitation - there is no approval step - so where the
// code goes matters. It goes to friends you know, privately. Never anywhere
// public: a code on social media is an open door into a group of teenagers.
// And because codes get screenshotted and forwarded anyway, anyone in the
// circle can swap it for a new one; the old one stops working at once and
// nobody already in is affected.

import { useState } from 'react';
import { Check, Copy, KeyRound, RefreshCw } from 'lucide-react';
import { useCirclesStore } from '../../stores/circlesStore';

const APP_URL = 'https://friendshipsvibe.divedeepdialogues.com';

export default function CircleInviteCard({ circle }) {
  const rotateInviteCode = useCirclesStore((s) => s.rotateInviteCode);
  const [copied, setCopied] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);

  const code = circle?.inviteCode || '';

  const copy = async (what, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(what);
      setTimeout(() => setCopied(''), 2000);
    } catch {
      setMessage({ kind: 'error', text: "Couldn't copy. You can select the code and copy it yourself." });
    }
  };

  const inviteMessage = `Want to do Friendships Vibe together? It's an app with short modules about friendships. We'd be in a circle: we read the same module, answer the same question, then talk about it.

To join:
1. Go to ${APP_URL}
2. Open Friend Circles, then Join a Circle
3. Enter the code: ${code}`;

  const newCode = async () => {
    setBusy(true);
    setMessage(null);
    const res = await rotateInviteCode(circle.id);
    setBusy(false);
    setConfirming(false);
    setMessage(res.success
      ? { kind: 'ok', text: 'Done. The old code no longer works. Everyone already in the circle is still in it.' }
      : { kind: 'error', text: res.error || "Couldn't get a new code. Try again in a moment." });
  };

  return (
    <section
      aria-labelledby="circle-invite-title"
      className="bg-gradient-to-br from-blue-500/10 to-purple-500/10 backdrop-blur-sm rounded-2xl p-5 sm:p-6 border border-purple-500/30"
    >
      <h2 id="circle-invite-title" className="text-lg font-bold text-white mb-3 flex items-center gap-2">
        <KeyRound aria-hidden="true" className="w-5 h-5 text-purple-300" />
        Invite friends
      </h2>

      <p className="text-purple-100 mb-2 text-sm font-medium">Your circle&apos;s code</p>
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="flex-1 bg-slate-900/50 rounded-lg px-4 py-3 font-mono text-2xl tracking-widest text-white text-center border border-purple-500/20">
          {code}
        </div>
        <button
          type="button"
          onClick={() => copy('code', code)}
          className="px-5 py-3 bg-purple-500 hover:bg-purple-600 text-white rounded-lg transition-colors inline-flex items-center justify-center gap-2"
        >
          {copied === 'code' ? <Check aria-hidden="true" className="w-5 h-5" /> : <Copy aria-hidden="true" className="w-5 h-5" />}
          {copied === 'code' ? 'Copied' : 'Copy code'}
        </button>
      </div>

      <ol className="space-y-2 text-sm text-purple-100 mb-4">
        <li className="flex gap-2">
          <span className="font-bold text-purple-300 flex-shrink-0">1.</span>
          <span>
            <button
              type="button"
              onClick={() => copy('message', inviteMessage)}
              className="underline underline-offset-2 hover:text-white"
            >
              {copied === 'message' ? 'Copied the invite message' : 'Copy an invite message'}
            </button>{' '}
            with the code in it.
          </span>
        </li>
        <li className="flex gap-2">
          <span className="font-bold text-purple-300 flex-shrink-0">2.</span>
          <span>Send it privately, to friends you know: a direct message or your own group chat. Never anywhere public.</span>
        </li>
        <li className="flex gap-2">
          <span className="font-bold text-purple-300 flex-shrink-0">3.</span>
          <span>They enter the code in the app and they&apos;re in. There&apos;s no approval step, so anyone with the code can join.</span>
        </li>
      </ol>

      <div className="rounded-xl bg-white/5 border border-white/10 p-4">
        <p className="text-sm text-slate-200">
          If the code has ended up somewhere it shouldn&apos;t, get a new one. The old one stops working
          straight away, and nobody already in the circle is affected. Anyone in the circle can do this.
        </p>
        {confirming ? (
          <div className="mt-3 flex flex-col sm:flex-row gap-2">
            <button
              type="button"
              onClick={newCode}
              disabled={busy}
              className="px-4 py-2.5 bg-purple-500 hover:bg-purple-600 text-white rounded-lg text-sm font-semibold disabled:opacity-50"
            >
              {busy ? 'Getting a new code…' : 'Yes, replace the code'}
            </button>
            <button
              type="button"
              onClick={() => setConfirming(false)}
              disabled={busy}
              className="px-4 py-2.5 bg-white/10 hover:bg-white/20 text-white rounded-lg text-sm"
            >
              Keep this one
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => { setConfirming(true); setMessage(null); }}
            className="mt-3 inline-flex items-center gap-2 px-4 py-2.5 bg-white/10 hover:bg-white/20 text-white rounded-lg text-sm"
          >
            <RefreshCw aria-hidden="true" className="w-4 h-4" />
            New code
          </button>
        )}
        {message && (
          <p
            role={message.kind === 'error' ? 'alert' : 'status'}
            className={`mt-3 text-sm ${message.kind === 'error' ? 'text-red-200' : 'text-teal-200'}`}
          >
            {message.text}
          </p>
        )}
      </div>
    </section>
  );
}
